import analyticsManifestJson from "./analyticsManifest.json";

export type JourneyMacro =
  (typeof analyticsManifestJson.journey_macros)[number];
export type LifecycleStage =
  (typeof analyticsManifestJson.lifecycle_stages)[number];
export type InteractionMode =
  (typeof analyticsManifestJson.interaction_modes)[number];
export type AudienceTier =
  (typeof analyticsManifestJson.audience_tiers)[number];
export type PathIdentityKey =
  (typeof analyticsManifestJson.path_identity_keys)[number];

/** Verbatim mirror of teamform-api/src/analytics/analyticsManifest.json */
export const analyticsManifest = analyticsManifestJson;

export const JOURNEY_MACROS = Object.freeze([
  ...analyticsManifest.journey_macros,
]) as ReadonlyArray<JourneyMacro>;

export const LIFECYCLE_STAGES = Object.freeze([
  ...analyticsManifest.lifecycle_stages,
]) as ReadonlyArray<LifecycleStage>;

export const INTERACTION_MODES = Object.freeze([
  ...analyticsManifest.interaction_modes,
]) as ReadonlyArray<InteractionMode>;

export const AUDIENCE_TIERS = Object.freeze([
  ...analyticsManifest.audience_tiers,
]) as ReadonlyArray<AudienceTier>;

export const REQUIRED_ENVELOPE_KEYS = Object.freeze([
  ...analyticsManifest.required_envelope_keys,
]);

export const PATH_IDENTITY_KEYS = Object.freeze([
  ...analyticsManifest.path_identity_keys,
]) as ReadonlyArray<PathIdentityKey>;

export const PATH_IDENTITY_VOCAB = Object.freeze(
  Object.fromEntries(
    Object.entries(analyticsManifest.path_identity_vocab).map(
      ([key, values]) => [key, Object.freeze([...values])]
    )
  )
) as Readonly<Record<PathIdentityKey, ReadonlyArray<string>>>;

type AmbiguousActionEntry = {
  id: string;
  identity_key: string;
  values: string[];
  graphql_operations?: string[];
  surfaces?: string[];
};

const freezeAmbiguousAction = (entry: AmbiguousActionEntry) =>
  Object.freeze({
    ...entry,
    values: Object.freeze([...entry.values]),
    ...(entry.graphql_operations
      ? {
          graphql_operations: Object.freeze([...entry.graphql_operations]),
        }
      : {}),
    ...(entry.surfaces ? { surfaces: Object.freeze([...entry.surfaces]) } : {}),
  });

export const AMBIGUOUS_ACTIONS = Object.freeze(
  analyticsManifest.ambiguous_actions.map((entry) =>
    freezeAmbiguousAction(entry as AmbiguousActionEntry)
  )
);

export const isJourneyMacro = (value: string): value is JourneyMacro =>
  (JOURNEY_MACROS as readonly string[]).includes(value);

export const isLifecycleStage = (value: string): value is LifecycleStage =>
  (LIFECYCLE_STAGES as readonly string[]).includes(value);

export const isPathIdentityValue = (key: string, value: string): boolean => {
  const allowed = PATH_IDENTITY_VOCAB[key as PathIdentityKey];
  return Array.isArray(allowed) && allowed.includes(value);
};
